// Demo tables, column by column. Single source for the PowerSync schema
// (./schema.ts), the row types below, and the test database.
// PowerSync column types are text, integer and real: booleans are stored as
// integer 0/1, JSON blobs as text. PowerSync adds the `id` text column itself.

export type DemoColumnType = 'text' | 'integer' | 'real';

interface DemoTableSpec {
  columns: Record<string, DemoColumnType>;
  indexed: readonly string[];
}

export const DEMO_TABLES = {
  demo_products: {
    columns: {
      bp_owner_id: 'text',
      bp_product_location_id: 'text',
      bp_product_barcode: 'text',
      bp_product_name: 'text',
      bp_product_category: 'text',
      bp_product_sub_category: 'text',
      bp_product_price_type: 'text',
      // 'product' | 'service'; rows saved before services read null (product)
      bp_product_kind: 'text',
      bp_product_unit_of_mass: 'text',
      bp_product_mass: 'text',
      bp_product_buying_price: 'real',
      bp_product_selling_price: 'real',
      bp_product_quantity: 'real',
      bp_product_size: 'text',
      bp_minimum_stock_level: 'real',
      bp_product_model: 'text',
      bp_product_image: 'text',
      bp_product_deleted: 'integer',
      bp_product_is_favourite: 'integer',
      bp_product_created_at: 'integer',
      bp_product_updated_at: 'integer',
      bp_product_has_variants: 'integer',
      bp_product_variant_attributes: 'text',
      bp_product_variants: 'text',
    },
    indexed: ['bp_product_name', 'bp_product_category'],
  },
  demo_cart_items: {
    columns: {
      product_id: 'text',
      variant_id: 'text',
      quantity: 'real',
      mass: 'real',
      unit_price: 'real',
      price_type: 'text',
      kind: 'text',
      name: 'text',
      unit_of_mass: 'text',
      category: 'text',
      image: 'text',
      created_at: 'integer',
    },
    indexed: ['product_id', 'variant_id'],
  },
  demo_order_master_information: {
    columns: {
      bp_order_master_owner_id: 'text',
      bp_order_master_employee_id: 'text',
      bp_order_customer_information: 'text',
      bp_order_customer_email_information: 'text',
      bp_order_customer_phone_number_information: 'text',
      bp_order_customer_information_updated_at: 'integer',
      bp_order_master_order_status: 'text',
      bp_order_master_payment_information: 'text',
      bp_order_master_receipt_reference_id: 'text',
      bp_order_master_receipt_ref_id_updated_at: 'integer',
      bp_sales_products_information: 'text',
      bp_order_master_created_at: 'integer',
      bp_order_master_updated_at: 'integer',
      bp_order_master_deleted_at: 'integer',
      bp_order_master_location_id: 'text',
      bp_order_master_order_total: 'real',
    },
    indexed: [
      'bp_order_master_owner_id',
      'bp_order_master_order_status',
      'bp_order_master_receipt_reference_id',
      'bp_order_master_location_id',
    ],
  },
  demo_payment_information: {
    columns: {
      bp_payment_status: 'integer',
      bp_payment_amount: 'real',
      bp_payment_amount_received: 'real',
      bp_payment_already_paid_amount: 'real',
      bp_payment_amount_actually_received: 'real',
      bp_payment_amount_due: 'real',
      bp_payment_amount_refunded: 'real',
      bp_payment_refunds: 'text',
      bp_order_information_id: 'text',
      bp_payment_created_at: 'integer',
      bp_payment_updated_at: 'integer',
      bp_payment_deleted_at: 'integer',
      bp_payments: 'text',
      bp_payment_location_id: 'text',
      bp_payment_method: 'text',
    },
    indexed: ['bp_order_information_id', 'bp_payment_location_id'],
  },
  demo_sales_information: {
    columns: {
      bp_employee_id: 'text',
      bp_owner_id: 'text',
      bp_order_id: 'text',
      bp_product_id: 'text',
      bp_order_quantity: 'real',
      bp_order_price: 'real',
      bp_order_paid_quantity: 'real',
      bp_order_completed: 'integer',
      bp_order_partial_paid: 'integer',
      bp_order_created_at: 'integer',
      bp_order_updated_at: 'integer',
      bp_order_deleted_at: 'integer',
      bp_order_mass: 'real',
      bp_order_paid_mass: 'real',
      bp_sales_location_id: 'text',
      bp_selling_price_changed: 'integer',
      bp_product_information: 'text',
      bp_order_price_type: 'text',
    },
    indexed: [
      'bp_owner_id',
      'bp_order_id',
      'bp_product_id',
      'bp_sales_location_id',
    ],
  },
} as const satisfies Record<string, DemoTableSpec>;

export type DemoTableName = keyof typeof DEMO_TABLES;

type DemoColumns<T extends DemoTableName> = (typeof DEMO_TABLES)[T]['columns'];

type DemoColumnValue<C> = C extends 'text' ? string | null : number | null;

/** A row as `SELECT *` returns it. */
export type DemoRow<T extends DemoTableName> = { id: string } & {
  [K in keyof DemoColumns<T>]: DemoColumnValue<DemoColumns<T>[K]>;
};

/** Column values for an insert or update (`id` is handled separately). */
export type DemoRowValues<T extends DemoTableName> = {
  [K in keyof DemoColumns<T>]?: DemoColumnValue<DemoColumns<T>[K]>;
};
